import { supabase } from '../lib/supabase';

// ─── Tenant Guard ────────────────────────────────────────────────────────────
// When an admin is impersonating an account, sessionStorage holds the target
// user's ID. We MUST use that ID — not the real auth session — so every query
// is scoped to the impersonated account's data, not the admin's data.
const tenantGuard = async () => {
  // Check for active impersonation session first
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) {
        return parsed?.targetId;
      }
    } catch (e) {
      // Malformed sessionStorage entry — fall through to real auth
    }
  }

  // No impersonation active — use the real authenticated user
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user?.id;
};

// Shared by services that create records linked to tenant-scoped forms.
// Admin impersonation must use the same target owner as form listing and writes.
export const getTenantUserId = tenantGuard;

// ─── Case Helpers ─────────────────────────────────────────────────────────────
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, l => `_${l?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const cls = error?.code?.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error?.message) {
    return [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ]?.some(p => p?.test(error?.message));
  }
  return false;
};

// ─── Service ──────────────────────────────────────────────────────────────────
export const formsService = {

  // LIST — only the target user's forms (impersonated or real)
  async getForms() {
    const userId = await tenantGuard();
    const { data, error } = await supabase?.from('forms')?.select('*')?.eq('user_id', userId)?.order('updated_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getForms error:', error?.message);
      return [];
    }
    return toCamelCase(data || []);
  },

  // READ — only if the form belongs to the target user
  async getFormById(formId) {
    const userId = await tenantGuard();
    const { data, error } = await supabase?.from('forms')?.select('*')?.eq('id', formId)?.eq('user_id', userId)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getFormById error:', error?.message);
      return null;
    }
    return data ? toCamelCase(data) : null;
  },

  // CREATE — user_id is always stamped from tenantGuard (impersonated or real)
  async createForm(formData) {
    const userId = await tenantGuard();

    const { id: _ignored, user_id: _alsoIgnored, userId: _camelIgnored, ...cleanData } = formData || {};

    const formId = `form_${Date.now()}_${Math.random()?.toString(36)?.substring(2, 9)}`;
    const now = new Date()?.toISOString();

    const row = {
      id: formId,
      ...toSnakeCase(cleanData),
      user_id: userId,               // ALWAYS overwrite with tenantGuard result
      created_at: now,
      updated_at: now,
    };

    const { data, error } = await supabase?.from('forms')?.insert(row)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('createForm error:', error?.message);
      return null;
    }
    return toCamelCase(data);
  },

  // UPDATE — scoped to target user; cannot touch another tenant's form
  async updateForm(formId, formData) {
    const userId = await tenantGuard();

    // Strip any attempt to change ownership
    const { user_id: _a, userId: _b, id: _c, ...safeData } = formData || {};

    const { data, error } = await supabase?.from('forms')?.update({ ...toSnakeCase(safeData), updated_at: new Date()?.toISOString() })?.eq('id', formId)?.eq('user_id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('updateForm error:', error?.message);
      return null;
    }
    return toCamelCase(data);
  },

  // DELETE — ownership verified before delete; scoped to target user
  async deleteForm(formId) {
    const userId = await tenantGuard();

    // Pre-flight ownership check
    const { data: owned, error: checkErr } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', userId)?.maybeSingle();

    if (checkErr || !owned) {
      console.error('deleteForm aborted: form not owned by target user', { formId, userId });
      return false;
    }

    const { error } = await supabase?.from('forms')?.delete()?.eq('id', formId)?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('deleteForm error:', error?.message);
      return false;
    }
    return true;
  },

  // DUPLICATE — uses getFormById (already tenant-scoped) then createForm (always stamps userId)
  async duplicateForm(formId) {
    const form = await this.getFormById(formId);
    if (!form) throw new Error('Form not found or access denied');

    const { id: _id, userId: _uid, ...rest } = form;
    return this.createForm({
      ...rest,
      title: `${form?.title} (Copy)`,
      status: 'draft',
      submissionsCount: 0,
    });
  },

  // REALTIME — filtered to target user at the channel level
  subscribeToForms(userId, callback) {
    const channel = supabase?.channel(`forms_changes_${userId}`)?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'forms', filter: `user_id=eq.${userId}` },
        (payload) => callback(toCamelCase(payload))
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};